// components/Sidebar.js - VERSION AVEC LANGUAGE TOGGLE
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  User, Settings, MessageCircle, Crown, 
  LogOut, X, Zap, Calendar, MessageSquare, Globe
} from 'lucide-react';
import styles from './Sidebar.module.css';

// RizzingBot logo image component
const RBLogo = ({ size = 48 }) => (
  <img
    src="/logo512.png"
    alt="RizzingBot"
    width={size}
    height={size}
    style={{ objectFit: 'contain', display: 'block' }}
  />
);

const Sidebar = ({ 
  user, 
  activeTab, 
  setActiveTab, 
  sidebarOpen, 
  setSidebarOpen 
}) => {
  const { t, i18n } = useTranslation(['sidebar', 'common']);
  const navigate = useNavigate();

  const handleLogout = () => {
    if (window.confirm(t('sidebar:logout_confirm'))) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/');
    }
  };

  const handleAccountNavigation = () => {
    navigate('/account');
    setSidebarOpen(false);
  };

  const handleSubscriptionNavigation = () => {
    navigate('/subscription');
    setSidebarOpen(false);
  };

  const handleSupportNavigation = () => {
    navigate('/support');
    setSidebarOpen(false);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSidebarOpen(false);
  };

  // Fonction pour changer la langue
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };

  const getDaysRemaining = () => {
    if (!user?.subscription?.expires_at) return 0;
    const now = new Date();
    const expiresAt = new Date(user.subscription.expires_at);
    const diffTime = expiresAt - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getInitials = () => {
    if (user?.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // ✅ CORRECTION : Mapping correct des plans
  const isStarter = user?.subscription?.plan_name === 'plan1';
  const isPremium = user?.subscription?.plan_name === 'plan2';
  const isPro = user?.subscription?.plan_name === 'plan3';
  const isFree = !isStarter && !isPremium && !isPro;

  const daysRemaining = getDaysRemaining();
  const remainingCredits = user?.remaining_credits || user?.credits || 0;

  const navigationItems = [
    { id: 'conversations', icon: MessageCircle, label: t('sidebar:nav.conversations') },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className={styles["sidebar-overlay"]}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div className={`${styles["sidebar"]} ${sidebarOpen ? styles['sidebar-open'] : ''}`}>
        {/* Close button */}
        <button
          onClick={() => setSidebarOpen(false)}
          className={styles["sidebar-close-button"]}
          aria-label={t('sidebar:close_menu')}
        >
          <X size={20} />
        </button>

        {/* Sidebar Header */}
        <div className={styles["sidebar-header"]}>
          {/* Brand Section */}
          <div className={styles["brand-section"]}>
            <div className={styles["brand-icon"]}>
              <RBLogo size={56} />
            </div>
            <div className={styles["brand-text"]}>
              <h1>RizzingBot</h1>
              <p>{t('sidebar:brand.tagline')}</p>
            </div>
          </div>

          {/* User Info Card */}
          <div 
            className={styles["user-info"]}
            onClick={handleAccountNavigation}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && handleAccountNavigation()}
          >
            <div className={styles["user-avatar"]}>
              {getInitials()}
            </div>
            <div className={styles["user-details"]}>
              <p className={styles["user-name"]}>{user?.username || t('sidebar:user.default_name')}</p>
              <p className={styles["user-email"]}>{user?.email || t('sidebar:user.default_email')}</p>
            </div>
          </div>
        </div>

        {/* Subscription Section */}
        <div className={styles["subscription-section"]}>
          <div className={styles["subscription-card"]}>
            {isPro ? (
              <>
                <div className={styles["subscription-icon"]}>👑</div>
                <h3>
                  <span className={styles["premium-badge"]}>
                    <Crown size={14} />
                    PRO
                  </span>
                </h3>
                <p>{t('sidebar:subscription.pro_plan')}</p>
                
                <div className={styles["credits-display"]}>
                  <Zap size={18} style={{ color: '#f59e0b' }} />
                  <strong>{remainingCredits.toLocaleString()}</strong>
                  <span>{t('sidebar:subscription.credits')}</span>
                </div>

                {daysRemaining > 0 && (
                  <div className={styles["expiry-info"]} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.25rem',
                    fontSize: '0.75rem',
                    color: '#78350f',
                    marginTop: '0.5rem'
                  }}>
                    <Calendar size={12} />
                    <span>{t('sidebar:subscription.days_remaining', { count: daysRemaining })}</span>
                  </div>
                )}

                <button 
                  onClick={handleSubscriptionNavigation}
                  className={styles["upgrade-button"]}
                >
                  <Crown size={16} />
                  {t('sidebar:subscription.manage')}
                </button>
              </>
            ) : isPremium ? (
              <>
                <div className={styles["subscription-icon"]}>💎</div>
                <h3>
                  <span className={styles["premium-badge"]} style={{
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                  }}>
                    <Crown size={14} />
                    PREMIUM
                  </span>
                </h3>
                <p>{t('sidebar:subscription.premium_plan')}</p>
                
                <div className={styles["credits-display"]}>
                  <Zap size={18} style={{ color: '#f59e0b' }} />
                  <strong>{remainingCredits.toLocaleString()}</strong>
                  <span>{t('sidebar:subscription.credits')}</span>
                </div>

                {daysRemaining > 0 && (
                  <div className={styles["expiry-info"]} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.25rem',
                    fontSize: '0.75rem',
                    color: '#78350f',
                    marginTop: '0.5rem'
                  }}>
                    <Calendar size={12} />
                    <span>{t('sidebar:subscription.days_remaining', { count: daysRemaining })}</span>
                  </div>
                )}

                <button 
                  onClick={handleSubscriptionNavigation}
                  className={styles["upgrade-button"]}
                >
                  <Crown size={16} />
                  {t('sidebar:subscription.manage')}
                </button>
              </>
            ) : isStarter ? (
              <>
                <div className={styles["subscription-icon"]}>⭐</div>
                <h3>
                  <span className={styles["premium-badge"]} style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  }}>
                    <Crown size={14} />
                    STARTER
                  </span>
                </h3>
                <p>{t('sidebar:subscription.starter_plan')}</p>
                
                <div className={styles["credits-display"]}>
                  <Zap size={18} style={{ color: '#f59e0b' }} />
                  <strong>{remainingCredits.toLocaleString()}</strong>
                  <span>{t('sidebar:subscription.credits')}</span>
                </div>

                {daysRemaining > 0 && (
                  <div className={styles["expiry-info"]} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.25rem',
                    fontSize: '0.75rem',
                    color: '#78350f',
                    marginTop: '0.5rem'
                  }}>
                    <Calendar size={12} />
                    <span>{t('sidebar:subscription.days_remaining', { count: daysRemaining })}</span>
                  </div>
                )}

                <button 
                  onClick={handleSubscriptionNavigation}
                  className={styles["upgrade-button"]}
                >
                  <Crown size={16} />
                  {t('sidebar:subscription.manage')}
                </button>
              </>
            ) : (
              <>
                <div className={styles["subscription-icon"]}>🆓</div>
                <h3>{t('sidebar:subscription.free_plan')}</h3>
                <p>{t('sidebar:subscription.limited_features')}</p>
                
                <div className={styles["credits-display"]}>
                  <Zap size={18} style={{ color: '#f59e0b' }} />
                  <strong>{remainingCredits}</strong>
                  <span>/ {user?.total_credits || 100} {t('sidebar:subscription.credits')}</span>
                </div>

                <button 
                  onClick={handleSubscriptionNavigation}
                  className={styles["upgrade-button"]}
                >
                  <Crown size={16} />
                  {t('sidebar:subscription.upgrade')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className={styles["sidebar-nav"]}>
          {/* Navigation principale */}
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleTabChange(item.id)}
              className={`${styles["nav-item"]} ${activeTab === item.id ? styles['nav-item-active'] : ''}`}
              aria-current={activeTab === item.id ? 'page' : undefined}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </button>
          ))}

          {/* Divider */}
          <div className={styles["nav-divider"]} />

          {/* Routes importantes */}
          <button
            onClick={handleSubscriptionNavigation}
            className={styles["nav-item"]}
          >
            <Crown size={20} />
            <span>{t('sidebar:nav.subscription')}</span>
          </button>

          <button
            onClick={handleSupportNavigation}
            className={styles["nav-item"]}
          >
            <MessageSquare size={20} />
            <span>{t('sidebar:nav.support')}</span>
          </button>

          {/* Settings & Logout */}
          <button
            onClick={handleAccountNavigation}
            className={styles["nav-item"]}
          >
            <Settings size={20} />
            <span>{t('sidebar:nav.settings')}</span>
          </button>

          <button
            onClick={handleLogout}
            className={`${styles["nav-item"]} ${styles["nav-item-logout"]}`}
          >
            <LogOut size={20} />
            <span>{t('sidebar:nav.logout')}</span>
          </button>
        </nav>

        {/* Language Switcher - EN BAS */}
        <div className={styles["sidebar-footer"]}>
          <div className={styles["language-switcher"]}>
            <div className={styles["language-label"]}>
              <Globe size={16} />
              <span>{t('sidebar:language.label')}</span>
            </div>
            {/* 
            <div className={styles["language-buttons"]}>
              <button
                className={`${styles["lang-btn"]} ${i18n.language === 'fr' ? styles["lang-btn-active"] : ''}`}
                onClick={() => changeLanguage('fr')}
                aria-label="Français"
              >
                FR
              </button>
              <button
                className={`${styles["lang-btn"]} ${i18n.language === 'en' ? styles["lang-btn-active"] : ''}`}
                onClick={() => changeLanguage('en')}
                aria-label="English"
              >
                EN
              </button>
            </div>
            */}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;